import React from "react";
import { toastWarning } from "../../../utils/methods";
import { NODE_CONSTANTS } from "../../../utils/appConstants";

export const PORT_SIZE_WITH_MARGIN = 32 + 5 * 2; // 32x32 image, margin of 5
export const PORT_SIZE_ON_GRID = 50;
export const PORT_SIZE_ON_PERIMETER = 40;
export const PORT_SIZE = {
  width: 32,
  height: 32,
};
export const PORT_GROUP_SIZE = {
  width: 50,
  height: 50,
};
export const BANDWIDTH_SIZE = PORT_SIZE;

export function adjustBandwidthPosition(object: { x: number; y: number }, element: HTMLDivElement) {
  const width = element.clientWidth - PORT_SIZE_WITH_MARGIN;
  const height = element.clientHeight - PORT_SIZE_WITH_MARGIN;
  const diffX = object.x > width / 2 ? width - object.x : object.x;
  const diffY = object.y > height / 2 ? height - object.y : object.y;
  if (diffX > diffY) object.y = object.y > height / 2 ? height : 0;
  else object.x = object.x > width / 2 ? width : 0;
}

function triggerMouseEvent(node: HTMLElement, eventType: string) {
  var clickEvent = document.createEvent("MouseEvents");
  clickEvent.initEvent(eventType, true, true);
  node.dispatchEvent(clickEvent);
}

export function forceDraggablePortMove(node: HTMLElement) {
  triggerMouseEvent(node, "mouseover");
  triggerMouseEvent(node, "mousedown");
  triggerMouseEvent(node, "mousemove");
  triggerMouseEvent(node, "mouseup");
  triggerMouseEvent(node, "click");
}

export function useHistory(stateToStore: number) {
  const [history, setHistory] = React.useState<number[]>([]);
  React.useEffect(() => {
    setHistory((prev: number[]) => [...prev, stateToStore]);
  }, [stateToStore]);
  const getScale = React.useCallback(() => {
    if (history.length < 2) return 1;
    const mostRecent = history[history.length - 1];
    const secondMostRecent = history[history.length - 2];
    return Math.pow(mostRecent / secondMostRecent, 2);
  }, [history]);
  return {
    getScale,
    history,
  };
}

export const getPortGrid = (height: number, width: number) => {
  return {
    y: Math.floor((height - NODE_CONSTANTS.PERIMETER_THICKNESS * 2) / PORT_SIZE_ON_GRID),
    x: Math.floor((width - NODE_CONSTANTS.PERIMETER_THICKNESS * 2) / PORT_SIZE_ON_GRID),
  };
};

export const getBandwidthPerimeter = (height: number, width: number) => {
  return {
    left: Math.floor((height - 2 * NODE_CONSTANTS.PERIMETER_THICKNESS) / PORT_SIZE_ON_PERIMETER),
    right: Math.floor((height - 2 * NODE_CONSTANTS.PERIMETER_THICKNESS) / PORT_SIZE_ON_PERIMETER),
    top: Math.floor(width / PORT_SIZE_ON_PERIMETER),
    bottom: Math.floor(width / PORT_SIZE_ON_PERIMETER),
  };
};

export function resetPortPositionGrid(node: any, height: number, width: number): { height: number; width: number } {
  // PORTS
  const allPorts = [];
  if (node.portsList) allPorts.push(...node.portsList);
  if (node.groups) allPorts.push(...node.groups);
  const allBandwidth = [];
  if (node.portsUsed) allBandwidth.push(...node.portsUsed);
  if (node.groupsUsed) allBandwidth.push(...node.groupsUsed);
  let tempWidthPorts, tempHeightPorts, portGrid: { x: number; y: number };
  tempHeightPorts = height - 100;
  tempWidthPorts = width - 100;

  do {
    tempHeightPorts += 100;
    tempWidthPorts += 100;
    portGrid = getPortGrid(tempHeightPorts, tempWidthPorts);
  } while (portGrid.x * portGrid.y < allPorts.length || tempHeightPorts > NODE_CONSTANTS.HEIGHT.MAX || tempWidthPorts > NODE_CONSTANTS.WIDTH.MAX);

  let tempWidthBandwidth, tempHeightBandwidth, bandwidthPerimeter: { left: number; right: number; top: number; bottom: number };
  tempHeightBandwidth = height - 100;
  tempWidthBandwidth = width - 100;

  do {
    tempHeightBandwidth += 100;
    tempWidthBandwidth += 100;
    bandwidthPerimeter = getBandwidthPerimeter(tempHeightBandwidth, tempWidthBandwidth);
  } while (
    bandwidthPerimeter.top + bandwidthPerimeter.bottom + bandwidthPerimeter.left + bandwidthPerimeter.right < allBandwidth.length ||
    tempHeightBandwidth > NODE_CONSTANTS.HEIGHT.MAX ||
    tempWidthBandwidth > NODE_CONSTANTS.WIDTH.MAX
  );

  const tempWidth = Math.max(tempWidthBandwidth, tempWidthPorts);
  const tempHeight = Math.max(tempHeightBandwidth, tempHeightPorts);

  portGrid = getPortGrid(tempHeight, tempWidth);
  bandwidthPerimeter = getBandwidthPerimeter(tempHeight, tempWidth);

  let portCounter = 0;
  allPorts.forEach((port: any) => {
    port.x = PORT_SIZE_ON_GRID * Math.floor(portCounter % portGrid.x);
    port.y = PORT_SIZE_ON_GRID * Math.floor(portCounter / portGrid.x);
    portCounter++;
  });

  const bandwidthCounter = { left: 0, right: 0, top: 0, bottom: 0 };
  allBandwidth.forEach((port: any) => {
    if (bandwidthCounter.left < bandwidthPerimeter.left) {
      port.x = 0;
      port.y = PORT_SIZE_ON_PERIMETER * bandwidthCounter.left + NODE_CONSTANTS.PERIMETER_THICKNESS;
      bandwidthCounter.left++;
    } else if (bandwidthCounter.top < bandwidthPerimeter.top) {
      port.x = PORT_SIZE_ON_PERIMETER * bandwidthCounter.top;
      port.y = 0;
      bandwidthCounter.top++;
    } else if (bandwidthCounter.right < bandwidthPerimeter.right) {
      port.x = width - PORT_SIZE_WITH_MARGIN;
      port.y = PORT_SIZE_ON_PERIMETER * bandwidthCounter.right + NODE_CONSTANTS.PERIMETER_THICKNESS;
      bandwidthCounter.right++;
    } else if (bandwidthCounter.bottom < bandwidthPerimeter.bottom) {
      port.x = PORT_SIZE_ON_PERIMETER * (bandwidthPerimeter.bottom - bandwidthCounter.bottom - 1);
      port.y = height - PORT_SIZE_WITH_MARGIN;
      bandwidthCounter.bottom++;
    }
  });
  return { height: tempHeight, width: tempWidth };
}

export function resetPortPositionGridDoNotChangeHeight(node: any, height: number, width: number) {
  // PORTS
  const allPorts = [];
  if (node.portsList) allPorts.push(...node.portsList);
  if (node.groups) allPorts.push(...node.groups);
  const allBandwidth = [];
  if (node.portsUsed) allBandwidth.push(...node.portsUsed);
  if (node.groupsUsed) allBandwidth.push(...node.groupsUsed);
  let error = false;

  const portGrid = getPortGrid(height, width);
  if (portGrid.x * portGrid.y < allPorts.length || height > NODE_CONSTANTS.HEIGHT.MAX || width > NODE_CONSTANTS.WIDTH.MAX) error = true;
  let portCounter = 0;
  allPorts.forEach((port: any) => {
    port.x = PORT_SIZE_ON_GRID * Math.floor(portCounter % portGrid.x);
    port.y = PORT_SIZE_ON_GRID * Math.floor(portCounter / portGrid.x);
    portCounter++;
  });

  const bandwidthPerimeter = getBandwidthPerimeter(height, width);
  if (
    bandwidthPerimeter.top + bandwidthPerimeter.bottom + bandwidthPerimeter.left + bandwidthPerimeter.right < allBandwidth.length ||
    height > NODE_CONSTANTS.HEIGHT.MAX ||
    width > NODE_CONSTANTS.WIDTH.MAX
  )
    error = true;
  const bandwidthCounter = { left: 0, right: 0, top: 0, bottom: 0 };
  allBandwidth.forEach((port: any) => {
    if (bandwidthCounter.left < bandwidthPerimeter.left) {
      port.x = 0;
      port.y = PORT_SIZE_ON_PERIMETER * bandwidthCounter.left + NODE_CONSTANTS.PERIMETER_THICKNESS;
      bandwidthCounter.left++;
    } else if (bandwidthCounter.top < bandwidthPerimeter.top) {
      port.x = PORT_SIZE_ON_PERIMETER * bandwidthCounter.top;
      port.y = 0;
      bandwidthCounter.top++;
    } else if (bandwidthCounter.right < bandwidthPerimeter.right) {
      port.x = width - PORT_SIZE_WITH_MARGIN;
      port.y = PORT_SIZE_ON_PERIMETER * bandwidthCounter.right + NODE_CONSTANTS.PERIMETER_THICKNESS;
      bandwidthCounter.right++;
    } else if (bandwidthCounter.bottom < bandwidthPerimeter.bottom) {
      port.x = PORT_SIZE_ON_PERIMETER * (bandwidthPerimeter.bottom - bandwidthCounter.bottom - 1);
      port.y = height - PORT_SIZE_WITH_MARGIN;
      bandwidthCounter.bottom++;
    }
  });
  if (error) toastWarning("Ports cannot fit inside node. Please expand the node or group your ports.");
}
